/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/prop-types */
import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle, useSyncExternalStore } from 'react';
import { Box, Alert, Button, Checkbox, CircularProgress, Stack, Typography, Switch, FormControlLabel, TextField } from '@mui/material';
import { shallowEqual } from 'react-redux';
import { resumeAttendanceCreation } from '../utils/attendanceActions.ts';
import { attendanceCoordinator } from '../hooks/useAttendanceCapture.ts';
import { clearIdentityCache } from '../utils/leadIdentityCache';
// Components
import { AttendedAppointment } from './Attended';
import { OpenDialogPopup, OpenAttendedPopup, OpenUnAttendedPopup } from './fullScreenPopUps';
import { PersonalDetails, TreatmentAndAppointment, PaymentOption, MessageAndReminder } from '../sections/@dashboard/general/app';
import { DentallyPaymentConfirmation, DentallyBookingFormPayment, DentallyBookingSuccess, DentallyTreatmentAvailabilityWizardStep, BookingModeToggle } from './dentally';
import { PmsAvailabilityWizardStep, PmsBookingConfirmStep } from './pms';
import PmsTreatmentSelector from './pms/PmsTreatmentSelector.tsx';
import Iconify from './Iconify';
import LeadIdentityConflictDialog from './LeadIdentityConflictDialog';
// Redux
import { setCurrentTreatmentDataGlobal } from '../redux/slices/multiTreatment';
import { clearLead, updateSteps, createNewLead, updateLeadDetail, addNotes, updateSendReminderDuringLeadCreate } from '../redux/slices/lead';
import { updateError } from '../redux/slices/user';
import { closeCreateLeadDialog, openCreateLeadDialog } from '../redux/slices/createLeadDialog';
import { dispatch, useSelector, store } from '../redux/store';
import { getDentallyConfigs } from '../redux/slices/practiceIntegration';
import {
    filterPracticeUserSelections,
    selectPracticeUsersForPractice,
} from '../redux/slices/practiceUser';
// hooks
import { useRecordAttendToggle, useUnRecordAttendToggle } from '../hooks';
import usePmsSchedulingScope from '../hooks/usePmsSchedulingScope';
import PmsSchedulingStatus from './pms/PmsSchedulingStatus';
import { useDentallyBooking } from '../hooks/useDentallyBooking';
import { localStorageRemoveItem } from '../utils/storage-available';
import { sectionCardStyle, sectionLabelStyle } from '../styles/DentallyBookingStyle';
import { CALL_FLOW_LEAD_CREATED_EVENT, createdLeadIdForCall } from './callFlow/callFlowModel';

// ── Dentally wizard step wrappers ──────────────────────────────────────────────
// Defined outside the main component so their identity is stable across renders.


// Keep mounted patient inputs during a same-practice permission refresh. The
// initial unknown mode never mounts an editable legacy form.
function BookingScopeStep({ blocked, status, children, frozen = false }) {
    const wasReady = useRef(false);
    if (!blocked) wasReady.current = true;
    return <>
        <Box component="fieldset" disabled={frozen} sx={{ display: blocked ? 'none' : 'block', border: 0, p: 0, m: 0, minWidth: 0, pointerEvents: frozen ? 'none' : 'auto' }}>{wasReady.current ? children : null}</Box>
        {blocked ? status : null}
    </>;
}

const BOOKING_PROGRESS_STEPS = [
    { index: 1, label: 'Creating lead' },
    { index: 2, label: 'Adding treatment' },
    { index: 3, label: 'Creating appointment' },
    { index: 4, label: 'Booking in Dentally' },
    { index: 5, label: 'Processing payment' },
];

const DentallyPaymentWizardStep = forwardRef(({
    dentallySlot, exportTrigger, onExportTriggerChange, dentallyPayment, onPaymentChange,
    dentallySubmit, dentallyTreatmentName, dentallyReason, dentallyAssignees,
    dentallyLoading, dentallyResult, dentallyError, bookingStep, onClose,
}, ref) => {
    const personalDetail = useSelector((state) => state.lead?.newLeadDetail?.personalDetail || {});
    const treatmentDetail = useSelector((state) => state.lead?.newLeadDetail?.treatmentDetail || {});
    const { practiceId: currentPracticeId } = useSelector((state) => state.practiceTreatmentList);
    const currentPracticeUsers = useSelector(
        (state) => selectPracticeUsersForPractice(state, currentPracticeId)
    );
    const identityResolution = useSelector((state) => state.createLeadDialog?.identity?.resolution || null);

    const resolvedEmail = personalDetail.lead_contact?.email || personalDetail.email || '';
    const resolvedPhone = personalDetail.lead_contact?.phone || personalDetail.phoneNumber || personalDetail.phone || '';
    const [contactEmail, setContactEmail] = useState(resolvedEmail);
    const [contactPhone, setContactPhone] = useState(resolvedPhone);

    const isPaymentRequired = exportTrigger === 'PAYMENT';
    const linkChannel = dentallyPayment.paymentLinkChannel || 'email';
    const needsEmail = dentallyPayment.paymentOption === 'link' && (linkChannel === 'email' || linkChannel === 'both');
    const needsPhone = dentallyPayment.paymentOption === 'link' && (linkChannel === 'whatsapp' || linkChannel === 'both');
    const canConfirm =
        dentallySlot &&
        !dentallySlot._manual &&
        !dentallyLoading &&
        !dentallyResult?.success &&
        (!isPaymentRequired || dentallyPayment.paymentOption !== 'none') &&
        (!needsEmail || contactEmail.trim()) &&
        (!needsPhone || contactPhone.trim());

    const handleConfirm = useCallback(async () => {
        if (!canConfirm) return;
        const scopedAssignees = filterPracticeUserSelections(
            currentPracticeUsers,
            dentallyAssignees || []
        );
        const startIso = dentallySlot.start_time;
        const finishIso = dentallySlot.finish_time;
        const durationMs = new Date(finishIso) - new Date(startIso);
        const durationMinutes = Math.round(durationMs / 60000) || 30;

        await dentallySubmit({
            // Everything the form collected, passed through whole. The flattened
            // fields below still win for the ones the confirm step can edit, but
            // hand-picking alone silently dropped lead_source, referral, ad
            // attribution, sex, is_registered_patient and several address lines.
            leadDetail: personalDetail,
            firstName: personalDetail.firstName || personalDetail.first_name || '',
            lastName: personalDetail.lastName || personalDetail.last_name || '',
            phone: contactPhone.trim() || resolvedPhone,
            email: contactEmail.trim() || resolvedEmail,
            salutation: personalDetail.salutation || '',
            dob: personalDetail.dob || '',
            sex: personalDetail.sex || '',
            // The operator's choice from the identity-check step (sent with the lead POST).
            identityResolution,
            postcode: personalDetail.lead_address?.postcode || personalDetail.postcode || '',
            addressLine1: personalDetail.lead_address?.address_line_1 || personalDetail.addressLine1 || '',
            treatmentName: dentallyTreatmentName || treatmentDetail?.treatment || treatmentDetail?.name || '',
            practitionerId: dentallySlot._queried_practitioner_id || dentallySlot.practitioner_id,
            reason: dentallyReason,
            dateTime: new Date(startIso),
            durationMinutes,
            method: 'IN_PRACTICE',
            assignees: scopedAssignees.map((a) => a.id),
            paymentOption: dentallyPayment.paymentOption || 'none',
            paymentAmount: dentallyPayment.paymentAmount,
            paymentType: dentallyPayment.paymentType || 'DEPOSIT',
            receivedMode: dentallyPayment.receivedMode,
            paymentLinkChannel: dentallyPayment.paymentLinkChannel || 'email',
        });
    }, [canConfirm, dentallySlot, dentallySubmit, personalDetail, identityResolution, contactPhone, contactEmail, resolvedPhone, resolvedEmail, dentallyTreatmentName, dentallyReason, treatmentDetail, currentPracticeUsers, dentallyAssignees, dentallyPayment]);

    useImperativeHandle(ref, () => ({
        submit() {
            if (!canConfirm) return null;
            handleConfirm();
            return null; // Don't advance wizard — success screen replaces content
        },
    }), [canConfirm, handleConfirm]);

    // ── Success view ───────────────────────────────────────────────────────────
    if (dentallyResult?.success) {
        return (
            <DentallyBookingSuccess
                result={dentallyResult}
                practiceId={currentPracticeId}
                onClose={onClose}
            />
        );
    }

    // ── Progress / loading view ────────────────────────────────────────────────
    if (dentallyLoading) {
        const currentIdx = bookingStep?.index || 0;
        const renderStepIcon = (index) => {
            if (currentIdx > index) return <Iconify icon='mdi:check-circle' width={18} sx={{ color: 'success.main', flexShrink: 0 }} />;
            if (currentIdx === index) return <CircularProgress size={16} thickness={5} sx={{ flexShrink: 0, color: 'success.main' }} />;
            return <Box sx={{ width: 18, height: 18, borderRadius: '50%', border: '2px solid', borderColor: 'divider', flexShrink: 0 }} />;
        };
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={44} sx={{ mb: 2, color: 'success.main' }} />
                <Typography variant='h6' fontWeight={700} sx={{ mb: 0.5 }}>
                    Booking in progress…
                </Typography>
                {bookingStep?.label && (
                    <Typography variant='body2' color='text.secondary' sx={{ mb: 3 }}>
                        {bookingStep.label}
                    </Typography>
                )}
                <Box sx={{ textAlign: 'left', maxWidth: 260, mx: 'auto' }}>
                    {BOOKING_PROGRESS_STEPS.map(({ index, label }) => (
                        <Stack key={index} direction='row' alignItems='center' spacing={1} sx={{ mb: 0.75 }}>
                            {renderStepIcon(index)}
                            <Typography variant='body2' sx={{
                                color: currentIdx >= index ? 'text.primary' : 'text.disabled',
                                fontWeight: currentIdx === index ? 600 : 400,
                            }}>
                                {label}
                            </Typography>
                        </Stack>
                    ))}
                </Box>
            </Box>
        );
    }

    // ── Normal booking form ────────────────────────────────────────────────────
    return (
        <Box>
            {/* Mode toggle — switch between payment-required and payment-optional */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                mb: 2, px: 1.5, py: 1, borderRadius: 1.5,
                bgcolor: isPaymentRequired ? 'rgba(255,171,0,0.08)' : 'rgba(0,171,85,0.06)',
                border: '1px solid',
                borderColor: isPaymentRequired ? 'rgba(255,171,0,0.24)' : 'rgba(0,171,85,0.16)',
            }}>
                <Stack direction='row' alignItems='center' spacing={1}>
                    <Iconify
                        icon={isPaymentRequired ? 'mdi:cash-lock' : 'mdi:calendar-check'}
                        width={18}
                        sx={{ color: isPaymentRequired ? '#FFAB00' : '#00AB55' }}
                    />
                    <Typography variant='body2' sx={{ fontWeight: 600, fontSize: 12 }}>
                        {isPaymentRequired ? 'Payment required before booking' : 'Book first — payment optional'}
                    </Typography>
                </Stack>
                <FormControlLabel
                    control={
                        <Switch
                            size='small'
                            checked={isPaymentRequired}
                            onChange={(e) => onExportTriggerChange(e.target.checked ? 'PAYMENT' : 'APPOINTMENT')}
                        />
                    }
                    label={<Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>Require payment</Typography>}
                    sx={{ m: 0 }}
                />
            </Box>

            <DentallyBookingFormPayment
                values={dentallyPayment}
                onChange={(field, val) => onPaymentChange((prev) => ({ ...prev, [field]: val }))}
                errors={{}}
            />
            {isPaymentRequired && dentallyPayment.paymentOption === 'none' && (
                <Alert severity='warning' sx={{ mb: 2 }}>
                    Payment required before appointment is confirmed.
                </Alert>
            )}

            {/* Contact details card — visible when sending payment link */}
            {dentallyPayment.paymentOption === 'link' && (
                <Box sx={sectionCardStyle}>
                    <Typography sx={sectionLabelStyle}>
                        <Iconify icon='mdi:account-circle-outline' width={15} sx={{ color: '#078DEE' }} />
                        Contact Details
                    </Typography>
                    <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1.5 }}>
                        Payment link will be sent to the email below. Verify details are correct.
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <TextField
                            size='small'
                            label='Email'
                            type='email'
                            value={contactEmail}
                            onChange={(e) => setContactEmail(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:email-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                        <TextField
                            size='small'
                            label='Phone'
                            value={contactPhone}
                            onChange={(e) => setContactPhone(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <Iconify icon='mdi:phone-outline' width={18} sx={{ color: 'text.disabled', mr: 1 }} />
                                ),
                            }}
                        />
                    </Box>
                    {!contactEmail.trim() && (
                        <Alert severity='warning' sx={{ mt: 1.5, mb: 0 }}>
                            Email is required to send a payment link.
                        </Alert>
                    )}
                </Box>
            )}

            {dentallyError && <Alert severity='error' sx={{ mb: 2 }}>{dentallyError.message}</Alert>}
        </Box>
    );
});

// Create lead opened from a call (call flow, CU-869fmc59c): tell the call flow which lead was
// saved, so it can link the call (and earlier ones) and carry on to the outcome screen.
// Read before the dialog state is cleared; nothing happens for a lead created any other way.
const reportCallLead = (status, id) => {
    const state = store.getState();
    const call = state.createLeadDialog?.call;
    if (!call) return;
    const leadId = createdLeadIdForCall({
        status,
        id,
        createLeadId: state.lead?.createLead?.id,
        baselineId: state.createLeadDialog?.baselineLeadId,
        existingSelection: state.lead?.newLeadDetail?.personalDetail?.existingLeadSelection,
    });
    if (leadId == null) return;
    window.dispatchEvent(new CustomEvent(CALL_FLOW_LEAD_CREATED_EVENT, { detail: { leadId, call } }));
};

// ── Main component ─────────────────────────────────────────────────────────────

const CreateLeadDialogWrapper = () => {
    const openContacts = useSelector((state) => state.createLeadDialog.open);

    const {
        attendance,
        markAsComplete,
        followUpLead,
        consentLink,
        isDentallyBooking,
    } = useSelector((state) => ({
        attendance: state.lead?.attendance,
        markAsComplete: state.lead?.markAsComplete,
        followUpLead: state.lead?.followUpLead,
        consentLink: state.lead?.consentLink,
        isDentallyBooking: state.lead?.isDentallyBooking,
    }), shallowEqual);
    const { dentallyEnabledGlobal } = useSelector((state) => state.practice);
    const pmsPersonalDetail = useSelector((state) => state.lead?.newLeadDetail?.personalDetail || null);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    useSyncExternalStore(attendanceCoordinator.subscribe, attendanceCoordinator.getSnapshot, attendanceCoordinator.getSnapshot);
    const attendanceCreationLocked = attendanceCoordinator.hasUnresolvedCreation();
    const pmsScope = usePmsSchedulingScope(practiceId);
    const pmsEnabledGlobal = pmsScope.enabled;
    const bookingPracticeRef = useRef(null);
    if (!openContacts) bookingPracticeRef.current = null;
    else if (bookingPracticeRef.current == null && practiceId) bookingPracticeRef.current = String(practiceId);
    const bookingPracticeChanged = bookingPracticeRef.current != null && bookingPracticeRef.current !== String(practiceId);
    const dentallyConfigs = useSelector((state) => state.practiceIntegration?.dentallyConfigs?.results || []);
    const configExportTrigger = dentallyConfigs.find((c) => c.is_active)?.dentally_export_trigger || 'APPOINTMENT';

    // Load Dentally config if not already in Redux. Gated on
    // dentallyEnabledGlobal: during post-login bootstrap the permissions
    // snapshot is empty and the thunk's hasFeatureAccess gate is
    // bootstrap-permissive, so an ungated mount fetch 403s for every
    // non-Dentally practice (same class of bug as the getPracticeItem probe).
    useEffect(() => {
        if (practiceId && dentallyEnabledGlobal && dentallyConfigs.length === 0) {
            dispatch(getDentallyConfigs(practiceId));
        }
    }, [practiceId, dentallyEnabledGlobal]); // eslint-disable-line react-hooks/exhaustive-deps

    const [exportTriggerOverride, setExportTriggerOverride] = useState(null);
    const exportTrigger = exportTriggerOverride || configExportTrigger;

    const {
        toggle: openRecordAttend,
        onClose: onCloseRecordAttend
    } = useRecordAttendToggle();
    const {
        toggle: openUnRecordAttend,
        onClose: onCloseUnRecordAttend
    } = useUnRecordAttendToggle();

    const handleAttendClose = () => onCloseRecordAttend();
    const handleUnAttendClose = () => onCloseUnRecordAttend();

    const inputRef = useRef();
    const [validStep, setValidStep] = useState(false);
    const [active, setActive] = useState(0);
    // Track follow-up mode in a ref so it survives clearLead() dispatched by
    // OpenContactLeadPopup's parallel handleClose chain before the thunk callback fires.
    const isFollowUpRef = useRef(false);

    const [dentallySlot, setDentallySlot] = useState(null);
    const [dentallyPayment, setDentallyPayment] = useState({ paymentOption: 'none' });
    const [selectedDentallyTreatment, setSelectedDentallyTreatment] = useState(null);
    // PMS scheduling state
    const [pmsTreatment, setPmsTreatment] = useState(null);
    const pmsExistingLead = pmsPersonalDetail?.existingLeadSelection || null;
    const [pmsSubmission, setPmsSubmission] = useState('idle');
    const pmsSubmissionRef = useRef('idle');
    const [pmsVerifiedAbsent, setPmsVerifiedAbsent] = useState(false);
    const updatePmsSubmission = useCallback((status) => {
        pmsSubmissionRef.current = status;
        setPmsSubmission(status);
    }, []);
    const beginPmsSubmission = useCallback(() => {
        if (pmsSubmissionRef.current !== 'idle' || bookingPracticeChanged || !pmsScope.ready) return false;
        updatePmsSubmission('pending');
        return true;
    }, [updatePmsSubmission, bookingPracticeChanged, pmsScope.ready]);
    const canNavigatePms = () => !['pending', 'unknown'].includes(pmsSubmissionRef.current);
    const [pmsSlot, setPmsSlot] = useState(null);
    const [pmsPractitioner, setPmsPractitioner] = useState(null);
    const [pmsDate, setPmsDate] = useState(null);
    const [pmsDuration, setPmsDuration] = useState(30);
    const [pmsConfirmStepState, setPmsConfirmStepState] = useState({});
    useEffect(() => { setPmsSlot(null); }, [pmsExistingLead?.id, pmsPersonalDetail?.first_name, pmsPersonalDetail?.last_name, pmsPersonalDetail?.dob, pmsPersonalDetail?.lead_contact?.email, pmsPersonalDetail?.lead_contact?.phone]);
    const [dentallyTreatmentName, setDentallyTreatmentName] = useState(null);
    const [dentallyReason, setDentallyReason] = useState('');
    const [dentallyAssignees, setDentallyAssignees] = useState([]);
    // Booking mode toggle — allows switching between Dentally and CRM-only flows
    const [useDentallyFlow, setUseDentallyFlow] = useState(true);
    const handleToggleBookingMode = useCallback((useDentally) => {
        setUseDentallyFlow(useDentally);
        if (!useDentally) {
            // Reset Dentally-specific state when switching to CRM-only
            setDentallySlot(null);
            setSelectedDentallyTreatment(null);
            setDentallyTreatmentName(null);
            setDentallyAssignees([]);
            setAvailSearchState({ selectedPractitioners: [], results: [], hasSearched: false, timeframePreset: '7days', duration: 30 });
        }
    }, []);

    // Availability search state — lifted here so it persists across wizard step navigation
    const [availSearchState, setAvailSearchState] = useState({
        selectedPractitioners: [],
        results: [],
        hasSearched: false,
        timeframePreset: '7days',
        duration: 30,
    });

    useEffect(() => {
        setDentallySlot(null);
        setSelectedDentallyTreatment(null);
        setDentallyTreatmentName(null);
        setDentallyReason('');
        setDentallyAssignees([]);
        setAvailSearchState({
            selectedPractitioners: [],
            results: [],
            hasSearched: false,
            timeframePreset: '7days',
            duration: 30,
        });
    }, [practiceId]);

    const {
        submit: dentallySubmit,
        result: dentallyResult,
        reset: dentallyReset,
        loading: dentallyLoading,
        error: dentallyError,
        bookingStep,
    } = useDentallyBooking({
        practiceId,
        existingLead: null,
        existingTreatmentId: null,
        isPaymentRequired: exportTrigger === 'PAYMENT',
    });

    const handleClose = (status, id, treatmentId) => {
        if (attendanceCoordinator.hasUnresolvedCreation()) {
            dispatch(updateError({ success: false, status: 409, message: 'This patient creation may have saved. Keep this form open and retry the original attendance confirmation to establish its result.' }));
            return;
        }
        if (!canNavigatePms()) return;
        if (status?.toLowerCase?.() === 'finish') {
            const isFollowUp = isFollowUpRef.current;
            setTimeout(() => {
                const storeState = store.getState();
                const { sendReminderDuringLeadCreate: shouldSendReminder } = storeState.lead;
                const currentPracticeId = storeState.practiceTreatmentList?.practiceId;
                if (shouldSendReminder) {
                    const details = {
                        additional_notes: "",
                        contacted_via: "PHONE",
                        lead: id,
                        note: "UNABLE_TO_CONTACT",
                        treatment: treatmentId
                    }
                    dispatch(addNotes(details, currentPracticeId, '', 'createLead'));
                    dispatch(updateSendReminderDuringLeadCreate(false))
                }
                const successMessage = isFollowUp
                    ? 'Follow-up recorded successfully!'
                    : 'Lead successfully created! Refresh the page or click \'Show Lead\' to view and manage it.';
                dispatch(updateError({ success: true, message: successMessage, lead: id, timeout: 10000 }));
            }, 5000);
        }
        isFollowUpRef.current = false;
        // A lead that was saved earlier in the wizard (booking steps still open) counts too.
        reportCallLead(status, id);
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(updateSteps(null));
        dispatch(closeCreateLeadDialog());
        clearIdentityCache();
        setPmsSlot(null);
        setPmsTreatment(null);
        updatePmsSubmission('idle');
        setPmsVerifiedAbsent(false);
        setPmsPractitioner(null);
        setPmsDate(null);
        setPmsConfirmStepState({});
        setDentallySlot(null);
        setDentallyPayment({ paymentOption: 'none' });
        setSelectedDentallyTreatment(null);
        setDentallyTreatmentName(null);
        setDentallyReason('');
        setDentallyAssignees([]);
        setAvailSearchState({ selectedPractitioners: [], results: [], hasSearched: false, timeframePreset: '7days', duration: 30 });
        setExportTriggerOverride(null);
        setUseDentallyFlow(true);
        dentallyReset();
    };

    // Called when user clicks "Close" on the Dentally booking success screen
    const handleDentallyClose = () => {
        // Save values before cleanup clears them
        const savedLeadId = dentallyResult?.leadId;
        const savedWarning = dentallyResult?.dentallyWarning;
        const savedPracticeId = practiceId; // capture before any cleanup
        reportCallLead('finish', savedLeadId);

        // Cleanup state immediately so dialog closes
        dispatch(clearLead());
        dispatch(setCurrentTreatmentDataGlobal(null));
        dispatch(updateSteps(null));
        dispatch(closeCreateLeadDialog());
        clearIdentityCache();
        setPmsSlot(null);
        setPmsTreatment(null);
        updatePmsSubmission('idle');
        setPmsVerifiedAbsent(false);
        setPmsPractitioner(null);
        setPmsDate(null);
        setPmsConfirmStepState({});
        setDentallySlot(null);
        setDentallyPayment({ paymentOption: 'none' });
        setSelectedDentallyTreatment(null);
        setDentallyTreatmentName(null);
        setDentallyReason('');
        setDentallyAssignees([]);
        setAvailSearchState({ selectedPractitioners: [], results: [], hasSearched: false, timeframePreset: '7days', duration: 30 });
        setExportTriggerOverride(null);
        setUseDentallyFlow(true);
        dentallyReset();

        // Show toast with "Show lead" — small delay so backend fully commits
        if (savedLeadId != null && savedPracticeId) {
            setTimeout(() => {
                dispatch(updateError({
                    success: true,
                    message: `Booking confirmed! Lead #${savedLeadId} created and appointment booked.${savedWarning ? ` Note: ${savedWarning}` : ''}`,
                    lead: savedLeadId,
                    timeout: 10000,
                }));
            }, 2000);
        }
    };

    const onSubmit = (data) => setValidStep(!!data);

    const STEPS = [
        {
            label: 'Personal details',
            component: <PersonalDetails nativeBooking={pmsEnabledGlobal && !dentallyEnabledGlobal} ref={inputRef} handleNext={onSubmit} active={active} identityCheck />,
        },
    ];

    // ── Confirm button label + icon based on payment option ────────────────────
    const CONFIRM_MAP = {
        received: { label: 'Confirm & Record Payment', icon: 'mdi:cash-check' },
        link: { label: 'Confirm & Send Link', icon: 'mdi:send-check' },
    };
    const { label: confirmLabel, icon: confirmIcon } = CONFIRM_MAP[dentallyPayment.paymentOption] || { label: 'Confirm Booking', icon: 'mdi:check-bold' };

    if (pmsEnabledGlobal && !dentallyEnabledGlobal) {
        STEPS.push({
            label: 'Select Appointment',
            component: (
                <Box>
                <PmsTreatmentSelector practiceId={practiceId} selected={pmsTreatment} onSelect={(treatment) => { setPmsTreatment(treatment); setPmsSlot(null); }} />
                <PmsAvailabilityWizardStep
                    ref={inputRef}
                    handleNext={onSubmit}
                    practiceId={practiceId}
                    treatmentId={pmsTreatment?.id}
                    leadId={pmsExistingLead?.id}
                    requireTreatment
                    pmsSlot={pmsSlot}
                    onSlotSelected={setPmsSlot}
                    selectedPractitioner={pmsPractitioner}
                    onPractitionerSelected={setPmsPractitioner}
                    selectedDate={pmsDate}
                    onDateSelected={setPmsDate}
                    appointmentDuration={pmsDuration}
                    onDurationChange={setPmsDuration}
                />
                </Box>
            ),
        });
        STEPS.push({
            label: 'Confirm Booking',
            ...pmsConfirmStepState,
            component: (
                <PmsBookingConfirmStep
                    ref={inputRef}
                    pmsSlot={pmsSlot}
                    practiceId={practiceId}
                    existingLead={pmsExistingLead}
                    existingTreatmentId={pmsTreatment?.id}
                    newPatient={pmsPersonalDetail}
                    submissionStatus={pmsSubmission}
                    beginSubmission={beginPmsSubmission}
                    onSubmissionStatusChange={updatePmsSubmission}
                    onClose={(success, result) => {
                        if (success) {
                            handleClose();
                            dispatch(updateError({ success: true, message: 'Appointment booked and patient record saved.', lead: result?.id }));
                        }
                    }}
                    onStepStateChange={setPmsConfirmStepState}
                    appointmentDuration={pmsDuration}
                />
            ),
        });
    } else if (dentallyEnabledGlobal && useDentallyFlow) {
        STEPS.push({
            label: 'Treatment & Availability',
            component: (
                <Box>
                    <BookingModeToggle useDentally onChange={handleToggleBookingMode} />
                    <DentallyTreatmentAvailabilityWizardStep
                        ref={inputRef}
                        handleNext={onSubmit}
                        practiceId={practiceId}
                        dentallySlot={dentallySlot}
                        onSlotSelected={setDentallySlot}
                        selectedDentallyTreatment={selectedDentallyTreatment}
                        assignees={dentallyAssignees}
                        onAssigneesChanged={setDentallyAssignees}
                        availSearchState={availSearchState}
                        onAvailSearchStateChange={setAvailSearchState}
                        onTreatmentSelected={(treatment) => {
                            setSelectedDentallyTreatment(treatment);
                            setDentallyTreatmentName(
                                treatment?.name || null
                            );
                            setDentallyPayment(prev => ({ ...prev, paymentAmount: undefined }));
                            setDentallyReason(treatment?.dentally_appointment_reason || '');
                        }}
                    />
                </Box>
            ),
        });
        STEPS.push({
            label: 'Confirm Booking',
            buttonLabel: confirmLabel,
            buttonIcon: confirmIcon,
            buttonDisabled: !dentallySlot || dentallySlot._manual || dentallyLoading || dentallyResult?.success || (exportTrigger === 'PAYMENT' && dentallyPayment.paymentOption === 'none'),
            buttonLoading: dentallyLoading,
            hideFooterOnSuccess: dentallyResult?.success,
            component: (
                <DentallyPaymentWizardStep
                    ref={inputRef}
                    dentallySlot={dentallySlot}
                    exportTrigger={exportTrigger}
                    onExportTriggerChange={setExportTriggerOverride}
                    dentallyPayment={dentallyPayment}
                    onPaymentChange={setDentallyPayment}
                    dentallySubmit={dentallySubmit}
                    dentallyTreatmentName={dentallyTreatmentName}
                    dentallyReason={dentallyReason}
                    dentallyAssignees={dentallyAssignees}
                    dentallyLoading={dentallyLoading}
                    dentallyResult={dentallyResult}
                    dentallyError={dentallyError}
                    bookingStep={bookingStep}
                    onClose={handleDentallyClose}
                />
            ),
        });
    } else {
        STEPS.push({
            label: 'Treatment & Appointment',
            component: (
                <Box>
                    {dentallyEnabledGlobal && <BookingModeToggle useDentally={false} onChange={handleToggleBookingMode} />}
                    <TreatmentAndAppointment type={'CreatLead'} ref={inputRef} handleNext={onSubmit} active={active} hideDentally />
                </Box>
            ),
        });
    }

    const [leadStep, setLeadStep] = useState(STEPS);
    const lastReadySteps = useRef(STEPS);
    if (pmsScope.ready && !bookingPracticeChanged) lastReadySteps.current = attendance ? leadStep : STEPS;
    const scopeWasReady = useRef(false);
    if (!openContacts) scopeWasReady.current = false;
    if (pmsScope.ready && !bookingPracticeChanged) scopeWasReady.current = true;
    const scopeBlocked = !pmsScope.ready || bookingPracticeChanged;
    const scopeStatus = bookingPracticeChanged
        ? <Alert severity="warning">Return to the original practice to continue this booking, or close it and start again. Patient details have not been transferred.</Alert>
        : <PmsSchedulingStatus scope={pmsScope} />;
    const scopedSteps = (scopeWasReady.current ? lastReadySteps.current : [lastReadySteps.current[0]]).map((step) => ({
        ...step,
        label: !scopeWasReady.current ? 'Booking settings' : step.label,
        buttonDisabled: attendanceCreationLocked || scopeBlocked || step.buttonDisabled,
        component: <BookingScopeStep frozen={attendanceCreationLocked} blocked={scopeBlocked} status={scopeStatus}>{step.component}</BookingScopeStep>,
    }));

    useEffect(() => {
        if (!pmsScope.ready || bookingPracticeChanged) return;
        if (attendance) {
            const steps = [...STEPS];
            if (attendance === 'attendance') {
                steps.push({
                    label: 'Appointment Outcome',
                    component: <AttendedAppointment ref={inputRef} handleNext={onSubmit} active={active} type={'CreatedLead'} />
                });
            } else {
                steps.push({
                    label: 'Payment options',
                    component: <PaymentOption ref={inputRef} handleNext={onSubmit} active={active} />
                });
                if (dentallyEnabledGlobal && isDentallyBooking) {
                    steps.push({
                        label: 'Dentally Confirmation',
                        component: <DentallyPaymentConfirmation ref={inputRef} handleNext={onSubmit} />
                    });
                }
                steps.push({
                    label: 'Message review & reminders',
                    component: <MessageAndReminder ref={inputRef} handleNext={onSubmit} active={active} type={'CreatLead'} />
                });
            }
            setLeadStep(steps);
        } else {
            setLeadStep(STEPS);
        }
    }, [attendance, isDentallyBooking, useDentallyFlow, pmsEnabledGlobal, dentallyEnabledGlobal, pmsScope.ready, bookingPracticeChanged]);

    // Keep step components fresh when slot, payment, or booking state changes
    useEffect(() => {
        if (attendance || !pmsScope.ready || bookingPracticeChanged) return;
        setLeadStep(STEPS);
    }, [dentallySlot, dentallyPayment, selectedDentallyTreatment, dentallyTreatmentName, dentallyAssignees, dentallyLoading, dentallyResult, dentallyError, bookingStep, availSearchState, useDentallyFlow, pmsPersonalDetail, pmsExistingLead, pmsTreatment, pmsSlot, pmsPractitioner, pmsDate, pmsDuration, pmsConfirmStepState, pmsSubmission, pmsEnabledGlobal, dentallyEnabledGlobal, pmsScope.ready, bookingPracticeChanged]);

    useEffect(() => {
        if (!markAsComplete || consentLink || !openContacts || !pmsScope.ready || bookingPracticeChanged) return;
        if (dentallyEnabledGlobal && useDentallyFlow) return; // Dentally submission is handled by DentallyPaymentWizardStep
        if (pmsEnabledGlobal && !dentallyEnabledGlobal) return; // PMS submission is handled by PmsBookingConfirmStep
        if (!followUpLead) {
            isFollowUpRef.current = false;
            dispatch(createNewLead(handleClose));
        } else if (followUpLead === 'followUp') {
            isFollowUpRef.current = true;
            dispatch(updateLeadDetail('finish', handleClose));
        }
    }, [markAsComplete, followUpLead, consentLink, openContacts]);

    const handleClickNewLead = () => {
        localStorageRemoveItem('localTreatmentData');
        dispatch(openCreateLeadDialog());
        dispatch(setCurrentTreatmentDataGlobal(null));
    };

    let recoveryContent;
    if (attendanceCreationLocked) recoveryContent = <Alert severity="warning" sx={{ m: 2 }}>This patient creation may have saved. These form details are preserved until the original request is confirmed.
                    <Button disabled={bookingPracticeChanged} onClick={resumeAttendanceCreation}>Retry original patient creation</Button>
                </Alert>;
    else recoveryContent = pmsSubmission === 'unknown' ? <Alert severity="warning" sx={{ m: 2 }}>
                    The previous booking may have completed. Keep these patient details while checking the diary and patient record.
                    <Button component="a" href={`/dashboard/practice/${practiceId}/scheduling?diary=1`} target="_blank" rel="noopener noreferrer">Open practice diary</Button>
                    <Button onClick={() => { updatePmsSubmission('success'); handleClose(); }}>Appointment found — close</Button>
                    <FormControlLabel control={<Checkbox checked={pmsVerifiedAbsent} onChange={(event) => setPmsVerifiedAbsent(event.target.checked)} />} label="I checked the diary and patient record and confirmed no appointment was created" />
                    <Button disabled={!pmsVerifiedAbsent} onClick={() => { setPmsSlot(null); setPmsVerifiedAbsent(false); updatePmsSubmission('idle'); }}>Allow another booking</Button>
                </Alert> : null;

    return (
        <>
            {openContacts && <OpenDialogPopup
                open={openContacts}
                allowExistingPatientFollowup={!(pmsEnabledGlobal && !dentallyEnabledGlobal)}
                navigationLocked={attendanceCreationLocked || !canNavigatePms()}
                navigationGuard={() => !attendanceCoordinator.hasUnresolvedCreation() && canNavigatePms()}
                recoveryContent={recoveryContent}
                onClose={handleClose}
                steps={scopedSteps}
                type={'createLead'}
                validStep={validStep}
                updateStep={(type) => setValidStep(type)}
                inputRef={inputRef}
                title='Create Lead'
                headerButtons
                setActive={setActive}
            />}
            {openRecordAttend && <OpenAttendedPopup
                open={openRecordAttend}
                onClose={handleAttendClose}
                type={'createLead'}
                title='Create Lead'
                headerButtons
            />}
            {openUnRecordAttend && <OpenUnAttendedPopup
                open={openUnRecordAttend}
                onClose={handleUnAttendClose}
                type={'createLead'}
                title='Create Lead'
                headerButtons
            />}
            <LeadIdentityConflictDialog />
            <Box id="new-lead-button" onClick={handleClickNewLead} sx={{ visibility: "hidden", position: "absolute" }} />
        </>
    );
};

export default CreateLeadDialogWrapper;
